import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Autocomplete, { autocompleteClasses, AutocompleteCloseReason } from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import {
    Briefcase,
    Building,
    ChevronDown,
    ChevronUp,
    CircleX,
    Clock,
    FileUp,
    Hash,
    MapPin,
    NotepadText,
    Search,
    UserRound,
    X,
} from 'lucide-react';
import { AppDispatch, useAppSelector } from '../../../redux/store/configureStore';
import { useDispatch } from 'react-redux-latest';
import { thunkUpdateSearch } from '../../../redux/ducks/prospect-search-filters/operations';
import { Button, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import { updateAllSelected } from '../../../redux/ducks/prospect-search-result/slice';
import { LocationDto, ProspectSearchAutoCompleteFields } from '@zaplify/prospects';
import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import debounce from 'lodash/debounce';
import { UploadCompaniesModalContainer } from '../../campaign/newCampaign/addProspects/prospectSearch/components/search-companies-modal/upload-companies-modal-container';
import { companySizes, industries } from '../../../enums/prospects';
import { UploadCompaniesProvider } from '../../../providers/upload-companies-provider';
import { useMsal } from '@azure/msal-react';

const COMPANY_LIMIT = 500;

export const CompanyNameFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state: any) => state.prospectSearchFilters['organizations']);
    const currentAndPastIncludeValue = useAppSelector(
        (state: any) => state.prospectSearchFilters['currentAndPastOrganizations']
    );
    const excludeValue = useAppSelector((state: any) => state.prospectSearchFilters['excludeOrganizations']);
    const currentAndPastExcludeValue = useAppSelector(
        (state: any) => state.prospectSearchFilters['excludeCurrentAndPastOrganizations']
    );
    const [nameInputValue, setNameInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);
    const [optionsFullData, setOptionsFullData] = React.useState([]);
    const [exclude, setExclude] = useState(false);
    const [loading, setLoading] = React.useState(false);
    const [displayUploadModal, setDisplayUploadModal] = React.useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const msal = useMsal();
    const [timeSpan, setTimeSpan] = React.useState<'current' | 'currentAndPast'>('current');

    const sdk = getZaplifySdk(apiEndpoint, msal.instance);

    const getCompanies = async (companyQuery) =>
        await sdk.profiles.sources.autocomplete({
            field: ProspectSearchAutoCompleteFields.JOB_COMPANY_NAME,
            query: companyQuery,
        });

    const handleCompanies = async (companyQuery) => {
        try {
            setLoading(true);
            const rawCompanies: any = await getCompanies(companyQuery);
            if (!rawCompanies || rawCompanies.length === 0) {
                setOptions([]);
                setOptionsFullData([]);
                return;
            }
            const companies = rawCompanies.map((c) => c);
            if (companies && companies.length > 0) {
                setOptions(companies.map((c) => c.name));
                setOptionsFullData(companies);
                return;
            }
        } catch (error) {
            console.log('failed to get companies');
        } finally {
            setLoading(false);
        }
    };

    const debouncedHandleCompanies = useCallback(
        debounce((nameInputValue) => handleCompanies(nameInputValue), 300),
        []
    );

    const updateInclude = (value: any[], timeSpanArg?: 'current' | 'currentAndPast') => {
        dispatch(updateAllSelected(false));
        const timeSpanToUse = timeSpanArg || timeSpan;
        if (timeSpanToUse === 'current') {
            dispatch(thunkUpdateSearch({ value: value, point: 'organizations' }));
        } else {
            dispatch(thunkUpdateSearch({ value: value, point: 'currentAndPastOrganizations' }));
        }
    };

    const updateExclude = (value: any[], timeSpanArg?: 'current' | 'currentAndPast') => {
        dispatch(updateAllSelected(false));
        const timeSpanToUse = timeSpanArg || timeSpan;
        if (timeSpanToUse === 'current') {
            dispatch(thunkUpdateSearch({ value: value, point: 'excludeOrganizations' }));
        } else {
            dispatch(thunkUpdateSearch({ value: value, point: 'excludeCurrentAndPastOrganizations' }));
        }
    };

    React.useEffect(() => {
        let active = true;

        if (nameInputValue === '') {
            return undefined;
        } else {
            setOptions([]);
            setOptionsFullData([]);
        }

        if (active) {
            debouncedHandleCompanies(nameInputValue);
        }

        return () => {
            active = false;
        };
        //eslint-disable-next-line
    }, [nameInputValue]);

    //const displayText = value?.length >= COMPANY_LIMIT ? `The search is limited to ${COMPANY_LIMIT} companies.` : null;

    const debouncedOnInputChange = debounce((newInputValue) => {
        setNameInputValue(newInputValue);
    }, 300);

    return (
        <>
            <UploadCompaniesProvider>
                <UploadCompaniesModalContainer
                    isOpen={displayUploadModal}
                    closeModal={() => setDisplayUploadModal(false)}
                    exclude={exclude}
                    includeValues={includeValue}
                    excludeValues={excludeValue}
                />
            </UploadCompaniesProvider>
            <Filter
                exclude={exclude}
                handleSetExcludeState={(value) => setExclude(value)}
                value={includeValue}
                excludeValue={excludeValue}
                setValue={updateInclude}
                setExcludeValue={updateExclude}
                label="Company"
                placeholder="Search for companies by name"
                options={optionsFullData}
                companyFilter
                onInputChange={(newInputValue) => {
                    debouncedOnInputChange(newInputValue);
                }}
                inputValue={nameInputValue}
                //includeInputInList
                getOptionLabel={(option) => option.name || option.inputValue || option}
                getValueLabel={(option) => option.name || option.inputValue || option}
                //upload
                freeSolo={!loading}
                icon={<Building strokeWidth={1} size={20} />}
                loading={loading}
                openUploadModal={() => setDisplayUploadModal(true)}
                // Current and past
                timeSpan={timeSpan}
                onSetTimeSpan={(value) => setTimeSpan(value)}
                currentAndPastExcludeValue={currentAndPastExcludeValue}
                currentAndPastValue={currentAndPastIncludeValue}
                isOpen={isOpen}
                toggleOpen={toggleOpen}
            />
        </>
    );
};

export const KeywordsFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['personalKeywords']);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludePersonalKeywords']);
    const [inputValue, setInputValue] = useState('');
    const [exclude, setExclude] = useState(false);
    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'personalKeywords' }));
    };

    const updateExclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'excludePersonalKeywords' }));
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    const handleSetValues = (value) => {
        if (exclude) {
            updateExclude(value);
        } else {
            updateInclude(value);
        }
    };

    return (
        <Filter
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            freeSolo
            onInputChange={(newInputValue) => {
                debouncedOnInputChange(newInputValue);
            }}
            value={includeValue}
            excludeValue={excludeValue}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            inputValue={inputValue}
            label="Prospect keywords"
            placeholder="Enter LinkedIn profile keywords"
            getOptionDisabled={(option) => {
                return exclude ? includeValue.includes(option) : excludeValue.includes(option);
            }}
            icon={<Hash strokeWidth={1} size={20} />}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const SummaryFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['personalSummary']);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludePersonalSummary']);
    const [inputValue, setInputValue] = useState('');
    const [exclude, setExclude] = useState(false);
    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'personalSummary' }));
    };

    const updateExclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'excludePersonalSummary' }));
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    return (
        <Filter
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            freeSolo
            onInputChange={(newInputValue) => {
                debouncedOnInputChange(newInputValue);
            }}
            value={includeValue}
            excludeValue={excludeValue}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            inputValue={inputValue}
            label="Profile summary"
            placeholder="Enter profile summary phrases"
            icon={<NotepadText strokeWidth={1} size={20} />}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const YearsOfExperienceFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const value = useAppSelector((state) => state.prospectSearchFilters['yearsOfExperience']);
    const dispatch: AppDispatch = useDispatch();

    const updateValue = (value: { min: number; max: number }) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'yearsOfExperience' }));
    };

    return (
        <RangeFilter
            label="Years of experience"
            setValue={updateValue}
            value={value}
            icon={<Briefcase strokeWidth={1} size={20} />}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const LocationFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['locations']);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludeLocations']);
    const [exclude, setExclude] = useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState<LocationDto[]>([]);
    const msal = useMsal();
    const sdk = getZaplifySdk(apiEndpoint, msal.instance);

    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'locations' }));
    };

    const updateExclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'excludeLocations' }));
    };

    const getLocations = async (locationQuery) => {
        try {
            return await sdk.profiles.sources.autocomplete({
                field: ProspectSearchAutoCompleteFields.LOCATION_NAME,
                query: locationQuery,
            });
        } catch (error) {
            console.log('error fetching locations');
        }
    };

    const handleLocations = async (locationQuery) => {
        try {
            setLoading(true);
            const rawLocations = await getLocations(locationQuery);
            let newOptions: LocationDto[] = [];

            if (rawLocations && rawLocations.length > 0) {
                newOptions = [...rawLocations];
            }
            //
            setOptions(newOptions);
        } catch (e) {
            console.log("couldn't fetch locations", e);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions([]);
            return undefined;
        }

        if (active) {
            handleLocations(inputValue);
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line
    }, [includeValue, inputValue, excludeValue]);

    const handleSetValues = (value) => {
        if (exclude) {
            updateExclude(value);
        } else {
            updateInclude(value);
        }
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && inputValue.trim() !== '') {
            const newValue = [{ name: inputValue.trim() }]; // Store input as an object

            if (exclude) {
                updateExclude(newValue);
            } else {
                updateInclude(newValue);
            }

            setInputValue(''); // Clear input field after selection
        }
    };

    const handleInputChange = (newInputValue: string) => {
        setInputValue(newInputValue); // Update input field
        handleLocations(newInputValue);

        // Detect Enter key press via event listener
        document.addEventListener("keydown", (event) => {
            if (event.key === "Enter" && newInputValue.trim() !== "") {
                const newValue = { name: newInputValue.trim() };

                if (exclude) {
                    const updateExcludeList = [...excludeValue, newValue]
                    updateExclude(updateExcludeList);
                } else {
                    const updateIncludeList = [...includeValue, newValue]
                    updateInclude(updateIncludeList);
                }

                setInputValue(""); // Clear input after adding
            }
        }, { once: true }); // Ensures event fires only once
    };

    return (
        <Filter
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            getOptionLabel={(option) => option?.name}
            getValueLabel={(option) => option?.name}
            label="Prospect location"
            placeholder="Enter location of prospects"
            options={options}
            onInputChange={handleInputChange} // Updated function

            //resetValueOnClick={false}
            loading={loading}
            inputValue={inputValue}
            value={includeValue}
            excludeValue={excludeValue}
            icon={<MapPin strokeWidth={1} size={20} />}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        /*getOptionDisabled={(option) => {
return exclude
? includeValue.includes((val) => val.name === option.name)
: excludeValue.includes((val) => val.name === option.name);
}}*/
        />
    );

    /*return (
        <Filter
            getOptionLabel={(option) => option.name}
            label="Prospect location"
            placeholder="Enter location of prospects"
            options={options as string[]}
            getOptionSelected={getOptionSelected}
            filterOptions={
                (options, state) => options.filter((option) => true) // Some results will be cleaned of foreign characters (e.g. ÅÄÖ).
            }
            onInputChange={(newInputValue) => {
                setInputValue(newInputValue);
            }}
            inputValue={inputValue}
            dataPoint="locations"
            excludeDataPoint="excludeLocations"
            icon={<Room />}
            resetOptions={() => setOptions([])}
            location={true}
            loading={loading}

    );/>*/
};

const getOptionSelected = (val, option) => (typeof val === 'string' ? val === option.name : val.name === option.name);

export const TitlesFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['occupationTitles']);
    const currentAndPastIncludeValue = useAppSelector(
        (state) => state.prospectSearchFilters['currentAndPastOccupationTitles']
    );
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludeOccupationTitles' || '']);
    const currentAndPastExcludeValue = useAppSelector(
        (state) => state.prospectSearchFilters['excludeCurrentAndPastOccupationTitles' || '']
    );
    const [exclude, setExclude] = useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [timeSpan, setTimeSpan] = React.useState<'current' | 'currentAndPast'>('current');
    const msal = useMsal();
    const sdk = getZaplifySdk(apiEndpoint, msal.instance);
    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[], timeSpanArg?: 'current' | 'currentAndPast') => {
        dispatch(updateAllSelected(false));
        const timeSpanToUse = timeSpanArg || timeSpan;
        if (timeSpanToUse === 'current') {
            dispatch(thunkUpdateSearch({ value: value, point: 'occupationTitles' }));
        } else {
            dispatch(thunkUpdateSearch({ value: value, point: 'currentAndPastOccupationTitles' }));
        }
    };

    const updateExclude = (value: any[], timeSpanArg?: 'current' | 'currentAndPast') => {
        dispatch(updateAllSelected(false));
        const timeSpanToUse = timeSpanArg || timeSpan;
        if (timeSpanToUse === 'current') {
            dispatch(thunkUpdateSearch({ value: value, point: 'excludeOccupationTitles' }));
        } else {
            dispatch(thunkUpdateSearch({ value: value, point: 'excludeCurrentAndPastOccupationTitles' }));
        }
    };

    useEffect(() => {
        let active = true;

        if (active && inputValue === '' && includeValue.length === 0 && excludeValue.length === 0) {
            setOptions([]);
            return;
        }

        if (active && inputValue) {
            handleTitles(inputValue);
        }

        if (active && inputValue === '' && includeValue.length) {
            handleTitles(includeValue);
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line
    }, [includeValue, inputValue, currentAndPastIncludeValue]);

    const getAutocompleteTitles = async (titlesQuery) =>
        await sdk.profiles.sources.autocomplete({
            field: ProspectSearchAutoCompleteFields.JOB_TITLE,
            query: titlesQuery,
        });
    const getSuggestedTitles = async (titlesQuery) => await sdk.profiles.sources.suggestTitles('titles', titlesQuery);

    const handleTitles = async (query) => {
        try {
            setLoading(true);
            let rawTitles;

            if (typeof query === 'string' && query !== '') rawTitles = await getAutocompleteTitles(query);
            else rawTitles = await getSuggestedTitles(query);

            let newOptions = [];

            if (rawTitles && rawTitles.length > 0) {
                newOptions = rawTitles;
            }
            setOptions(newOptions);
        } catch (error) {
            console.log('title search error');
        } finally {
            setLoading(false);
        }
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    return (
        <Filter
            freeSolo
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            label="Job title"
            placeholder="Enter job titles"
            options={options}
            onInputChange={(newInputValue) => debouncedOnInputChange(newInputValue)}
            //resetValueOnClick={false}
            inputValue={inputValue}
            loading={loading}
            value={includeValue}
            currentAndPastValue={currentAndPastIncludeValue}
            excludeValue={excludeValue}
            currentAndPastExcludeValue={currentAndPastExcludeValue}
            icon={<Briefcase strokeWidth={1} size={20} />}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            getOptionDisabled={(option) => {
                return exclude ? includeValue.includes(option) : excludeValue.includes(option);
            }}
            timeSpan={timeSpan}
            onSetTimeSpan={(value) => setTimeSpan(value)}
            //loading={loading}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const RoleFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['roles'] || []);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludeRoles'] || []);
    const [exclude, setExclude] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        if (!Array.isArray(value)) return;
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value, point: 'roles' }));
    };

    const updateExclude = (value: any[]) => {
        if (!Array.isArray(value)) return;
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value, point: 'excludeRoles' }));
    };

    const handleSetValues = (value: any[]) => {
        if (!value || value.length === 0) return;
        exclude ? updateExclude(value) : updateInclude(value);
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    return (
        <Filter
            onInputChange={(newInputValue) => {
                debouncedOnInputChange(newInputValue);
            }}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            value={includeValue}
            excludeValue={excludeValue}
            inputValue={inputValue}
            options={['Manager', 'Developer', 'Designer', 'Sales', 'Marketing']}
            label="Worko Role"
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            placeholder="Select roles"
            icon={<Briefcase strokeWidth={1} size={20} />}
            getOptionLabel={(option) => option || ''}
            getValueLabel={(option) => option || ''}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionDisabled={(option) => {
                return exclude ? includeValue.includes(option) : excludeValue.includes(option);
            }}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const IndustriesFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['industries']);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['excludeIndustries' || '']);
    const [exclude, setExclude] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'industries' }));
    };

    const updateExclude = (value: any[]) => {
        dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'excludeIndustries' }));
    };

    const handleSetValues = (value) => {
        if (exclude) {
            updateExclude(value);
        } else {
            updateInclude(value);
        }
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    return (
        <Filter
            onInputChange={(newInputValue) => {
                debouncedOnInputChange(newInputValue);
            }}
            setValue={updateInclude}
            setExcludeValue={updateExclude}
            value={includeValue}
            excludeValue={excludeValue}
            inputValue={inputValue}
            options={industries}
            label="Industry"
            exclude={exclude}
            handleSetExcludeState={(value) => setExclude(value)}
            placeholder="Select industries"
            icon={<Briefcase strokeWidth={1} size={20} />}
            getOptionDisabled={(option) => {
                return exclude ? includeValue.includes(option) : excludeValue.includes(option);
            }}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

export const OrganizationSizesFilter = ({ isOpen, toggleOpen }: { isOpen: boolean; toggleOpen: () => void }) => {
    const includeValue = useAppSelector((state) => state.prospectSearchFilters['organizationSizes']);
    const excludeValue = useAppSelector((state) => state.prospectSearchFilters['organizationSizes' || '']);
    const [exclude, setExclude] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const dispatch: AppDispatch = useDispatch();

    const updateInclude = (value: any[]) => {
        //dispatch(updateAllSelected(false));
        dispatch(thunkUpdateSearch({ value: value, point: 'organizationSizes' }));
    };

    const debouncedOnInputChange = debounce((newInputValue) => {
        setInputValue(newInputValue);
    }, 300);

    return (
        <Filter
            onInputChange={(newInputValue) => {
                debouncedOnInputChange(newInputValue);
            }}
            setValue={updateInclude}
            inputValue={inputValue}
            options={companySizes}
            label="Employee count"
            placeholder="Select number of employees"
            noExclude
            value={exclude ? excludeValue : includeValue}
            icon={<UserRound strokeWidth={1} size={20} />}
            hideSearch
            isOpen={isOpen}
            toggleOpen={toggleOpen}
        />
    );
};

interface LabelType {
    name: string;
    color: string;
    description?: string;
}

type FilterProps = {
    onInputChange: (value: string) => void;
    inputValue: string;
    options?: any[];
    value: string[] | any[];
    currentAndPastValue?: string[] | any[];
    excludeValue?: string[] | any[];
    currentAndPastExcludeValue?: string[] | any[];
    setValue: (value: string[] | any[], timeSpan?: 'current' | 'currentAndPast') => void;
    setExcludeValue?: (value: string[] | any[], timeSpan?: 'current' | 'currentAndPast') => void;
    label: string;
    placeholder: string;
    noExclude?: boolean;
    exclude?: boolean;
    handleSetExcludeState?: (value: boolean) => void;
    icon?: React.ReactNode;
    freeSolo?: boolean;
    getOptionLabel?: (option) => string;
    getValueLabel?: (option) => string;
    isOptionEqualToValue?: (option, value) => boolean;
    getOptionDisabled?: (option) => boolean;
    companyFilter?: boolean;
    loading?: boolean;
    openUploadModal?: () => void;
    hideSearch?: boolean;
    timeSpan?: 'current' | 'currentAndPast';
    onSetTimeSpan?: (value: 'current' | 'currentAndPast') => void;
    isOpen?: boolean;
    toggleOpen?: () => void;
};

export const Filter = ({
    onInputChange,
    inputValue,
    options,
    value,
    setValue,
    setExcludeValue,
    excludeValue,
    label,
    placeholder,
    noExclude,
    exclude,
    handleSetExcludeState,
    getOptionDisabled,
    getOptionLabel,
    getValueLabel,
    icon,
    freeSolo,
    isOptionEqualToValue,
    companyFilter,
    loading,
    openUploadModal,
    timeSpan,
    onSetTimeSpan,
    currentAndPastValue = [],
    currentAndPastExcludeValue = [],
    isOpen,
    toggleOpen,
}: FilterProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showAllIncluded, setShowAllIncluded] = React.useState<boolean>(false);
    const [showAllExcluded, setShowAllExcluded] = React.useState<boolean>(false);
    const theme = useTheme();
    const open = isOpen;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        value && setValue(value, 'current');
        timeSpan && setValue(currentAndPastValue, 'currentAndPast');
        setExcludeValue && setExcludeValue(excludeValue, 'current');
        timeSpan && setExcludeValue(currentAndPastExcludeValue, 'currentAndPast');
        toggleOpen();

        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        if (anchorEl) {
            anchorEl.focus();
        }
        toggleOpen();
        setAnchorEl(null);
    };

    const getOptions = () => {
        if (!options?.length && !freeSolo) return [];
        if (!options?.length && freeSolo && !inputValue) return [];

        //if (!options?.length && freeSolo && inputValue) return [`Add "${inputValue}"`];
        //if (options?.length && freeSolo && inputValue) return [...options, `Add "${inputValue}"`];

        return options || [];
    };

    const handleClearClick = (e) => {
        e.stopPropagation();
        if (timeSpan) {
            setValue([], 'current');
            setValue([], 'currentAndPast');
            setExcludeValue && setExcludeValue([], 'current');
            setExcludeValue && setExcludeValue([], 'currentAndPast');
        } else {
            setValue([]);
            setExcludeValue && setExcludeValue([]);
        }
    };

    const newOptions = getOptions();

    // const open = Boolean(anchorEl);
    const id = open ? 'github-label' : undefined;
    const combinedValue = [
        ...(value || []),
        ...(excludeValue || []),
        ...(currentAndPastValue || []),
        ...(currentAndPastExcludeValue || []),
    ];
    const haveFilters = !!combinedValue?.length;

    return (
        <React.Fragment>
            <ButtonBase
                sx={{
                    display: 'flex',
                    height: '40px',
                    padding: 'var(--S-s-regular, 8px) var(--S-s-spacious, 12px)',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 'var(--S-xs, 6px)',
                    borderRadius: 'var(--radius-round, 100px)',
                    background: 'var(--grey-palette-grey-6, #3A4B59)',
                    boxShadow: '0px 1px 2px 0px rgba(20, 21, 26, 0.06)',
                    color: 'var(--grey-palette-white, #FFF)',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily:
                        '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu","Cantarell", "Fira Sans", "Droid Sans", sans-serif !important',
                    '& svg': {
                        color: 'white',
                    },
                    '&.active': {
                        background: 'var(--grey-palette-grey-6, #3A4B59)',
                        color: 'var(--grey-palette-white, #FFF)',
                        '&:hover': {
                            background: 'var(--grey-palette-grey-6, #495e6f)',
                        },
                    },
                    '&.in-active': {
                        background: 'var(--grey-palette-white, #FFF)',
                        color: 'var(--grey-palette-grey-6, #3A4B59)',
                        border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                        '&:hover': {
                            background: 'rgba(21, 27, 38, 0.04)',
                        },
                        '& svg': {
                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                        },
                    },
                }}
                onClick={handleClick}
                className={haveFilters ? 'active' : 'in-active'}
            >
                {icon}
                {!haveFilters && label}
                {haveFilters &&
                    combinedValue.slice(0, 1).map((label) => (
                        <Box
                            key={label}
                            sx={{
                                color: 'var(--grey-palette-white, #FFF)',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '21px /* 150% */',
                                maxWidth: '200px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'left',
                            }}
                        >
                            {getValueLabel ? getValueLabel(label) : label}
                        </Box>
                    ))}
                {combinedValue?.slice(1).length > 0 && (
                    <Box
                        sx={{
                            color: 'var(--grey-palette-white, #FFF)',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '21px /* 150% */',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        + {combinedValue?.slice(1).length}
                    </Box>
                )}
                {!haveFilters && !open && <ChevronDown strokeWidth={1} size={20} />}
                {!haveFilters && open && <ChevronUp strokeWidth={1} size={20} />}
                {haveFilters && (
                    <IconButton size="small" onClick={handleClearClick}>
                        <CircleX strokeWidth={1} size={20} />
                    </IconButton>
                )}
            </ButtonBase>
            <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" transition={false}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box>
                        <Autocomplete
                            open
                            freeSolo={freeSolo}
                            multiple
                            onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                                if (reason === 'escape') {
                                    handleClose();
                                }
                            }}
                            value={
                                timeSpan === 'currentAndPast'
                                    ? exclude
                                        ? currentAndPastExcludeValue
                                        : currentAndPastValue
                                    : exclude
                                        ? excludeValue
                                        : value
                            }
                            onChange={(event, newValue, reason) => {
                                let tempValues = newValue;
                                if (freeSolo) {
                                    tempValues = newValue.map((v) => v.inputValue || v);
                                }
                                if (
                                    event.type === 'keydown' &&
                                    ((event as React.KeyboardEvent).key === 'Backspace' ||
                                        (event as React.KeyboardEvent).key === 'Delete') &&
                                    reason === 'removeOption'
                                ) {
                                    return;
                                }
                                if (timeSpan === 'currentAndPast') {
                                    exclude
                                        ? setExcludeValue(tempValues, 'currentAndPast')
                                        : setValue(tempValues, 'currentAndPast');
                                } else {
                                    exclude ? setExcludeValue(tempValues) : setValue(tempValues);
                                }
                            }}
                            isOptionEqualToValue={isOptionEqualToValue}
                            disableCloseOnSelect
                            PopperComponent={PopperComponent}
                            getOptionLabel={getOptionLabel}
                            renderTags={() => null}
                            getOptionDisabled={getOptionDisabled}
                            filterOptions={(options, params) =>
                                filterOptionsHandler(getOptionLabel, freeSolo, options, params)
                            }
                            noOptionsText={inputValue ? 'No results found' : 'Start your search by typing'}
                            includeInputInList
                            loading={loading}
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    {companyFilter && (
                                        <Box
                                            className="company-filter-item"
                                            data-cy="company-filter-item"
                                            sx={{
                                                display: 'flex',
                                                gap: '8px',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                component={DoneIcon}
                                                sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                                                style={{
                                                    visibility: selected ? 'visible' : 'hidden',
                                                }}
                                            />
                                            <Box
                                                className="logo-container"
                                                sx={{
                                                    '& img': {
                                                        width: '30px',
                                                    },
                                                }}
                                            >
                                                <img
                                                    className="logo"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).onerror = null;
                                                        (e.target as HTMLImageElement).src = '/images/no-logo-icon.svg';
                                                    }}
                                                    src={
                                                        option.website || ''
                                                            ? `https://logo.clearbit.com/${option.website || ''}`
                                                            : '/images/no-logo-icon.svg'
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                className={`label-container ${!option.name ? 'no-company' : ''}`}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    '& p': {
                                                        margin: 0,
                                                    },
                                                    '& .company': {
                                                        fontSize: 15,
                                                        textTransform: 'capitalize',
                                                        fontWeight: 500,
                                                    },
                                                    '& .domain': {
                                                        color: 'var(--grey-palette-grey-5, #6B7985)',
                                                        fontSize: 14,
                                                    },
                                                }}
                                            >
                                                <p className="company">{getOptionLabel(option)}</p>
                                                {option.name && (
                                                    <p
                                                        className={`domain ${formatDomain(option.website) === 'No website'
                                                            ? 'no-domain'
                                                            : ''
                                                            }`}
                                                    >
                                                        {formatDomain(option.website)}
                                                    </p>
                                                )}
                                            </Box>
                                            <Box
                                                component={CloseIcon}
                                                sx={{ opacity: 0.6, width: 18, height: 18, marginLeft: 'auto' }}
                                                style={{
                                                    visibility: selected ? 'visible' : 'hidden',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {!companyFilter && (
                                        <>
                                            <Box
                                                component={DoneIcon}
                                                sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                                                style={{
                                                    visibility: selected ? 'visible' : 'hidden',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    '& span': {
                                                        color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                                                    },
                                                }}
                                            >
                                                {getOptionLabel ? getOptionLabel(option) : option.title || option}
                                            </Box>
                                            <Box
                                                component={CloseIcon}
                                                sx={{ opacity: 0.6, width: 18, height: 18 }}
                                                style={{
                                                    visibility: selected ? 'visible' : 'hidden',
                                                }}
                                            />
                                        </>
                                    )}
                                </li>
                            )}
                            options={newOptions}
                            renderInput={(params) => (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            padding: 'var(--S-2xs, 4px)',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: '4px',
                                            alignSelf: 'stretch',
                                            borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            paddingRight: '8px',
                                        }}
                                    >
                                        <StyledInput
                                            startAdornment={<Search strokeWidth={1} size={20} />}
                                            ref={params.InputProps.ref}
                                            inputProps={params.inputProps}
                                            autoFocus
                                            placeholder={placeholder}
                                            value={inputValue}
                                            onChange={(event) => {
                                                onInputChange(event.target.value);
                                            }}
                                        />
                                        {companyFilter && (
                                            <Tooltip title="Upload a list of companies to search for">
                                                <IconButton onClick={openUploadModal}>
                                                    <FileUp strokeWidth={1} size={24} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    {timeSpan !== undefined && (
                                        <Box
                                            className="include-switch-container"
                                            sx={{
                                                display: 'flex',
                                                padding: 'var(--S-s-regular, 8px)',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                                alignSelf: 'stretch',
                                                borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 'var(--S-2xs, 4px)',
                                                    alignSelf: 'stretch',
                                                }}
                                            >
                                                <Button
                                                    onClick={() => onSetTimeSpan('current')}
                                                    sx={{
                                                        display: 'flex',
                                                        height: '24px',
                                                        padding: '9px var(--S-s-spacious, 12px)',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flex: '1 0 0',
                                                        borderRadius: '100px',
                                                        '&.active': {
                                                            background: 'var(--grey-palette-grey-6, #3A4B59)',
                                                            color: 'var(--grey-palette-white, #FFF)',
                                                        },
                                                        '&.in-active': {
                                                            border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                            background: 'var(--grey-palette-white, #FFF)',
                                                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                                                        },
                                                    }}
                                                    className={
                                                        'include-switch' +
                                                        (timeSpan === 'currentAndPast' ? ' in-active' : ' active')
                                                    }
                                                >
                                                    <span>Current</span>
                                                </Button>
                                                <Button
                                                    onClick={() => onSetTimeSpan('currentAndPast')}
                                                    sx={{
                                                        display: 'flex',
                                                        height: '24px',
                                                        padding: '9px var(--S-s-spacious, 12px)',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flex: '1 0 0',
                                                        borderRadius: '100px',
                                                        '&.active': {
                                                            background: 'var(--grey-palette-grey-6, #3A4B59)',
                                                            color: 'var(--grey-palette-white, #FFF)',
                                                        },
                                                        '&.in-active': {
                                                            border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                            background: 'var(--grey-palette-white, #FFF)',
                                                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                                                        },
                                                    }}
                                                    className={
                                                        'exclude-switch' +
                                                        (timeSpan === 'currentAndPast' ? ' active' : ' in-active')
                                                    }
                                                >
                                                    <span>Current & Past</span>
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                    {!noExclude && (
                                        <Box
                                            className="include-switch-container"
                                            sx={{
                                                display: 'flex',
                                                padding: 'var(--S-s-regular, 8px)',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                                alignSelf: 'stretch',
                                                borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 'var(--S-2xs, 4px)',
                                                    alignSelf: 'stretch',
                                                }}
                                            >
                                                <Button
                                                    onClick={() => handleSetExcludeState(false)}
                                                    sx={{
                                                        display: 'flex',
                                                        height: '24px',
                                                        padding: '9px var(--S-s-spacious, 12px)',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flex: '1 0 0',
                                                        borderRadius: '100px',
                                                        '&.active': {
                                                            background: 'var(--grey-palette-grey-6, #3A4B59)',
                                                            color: 'var(--grey-palette-white, #FFF)',
                                                        },
                                                        '&.in-active': {
                                                            border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                            background: 'var(--grey-palette-white, #FFF)',
                                                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                                                        },
                                                    }}
                                                    className={'include-switch' + (exclude ? ' in-active' : ' active')}
                                                >
                                                    <span>Include</span>
                                                </Button>
                                                <Button
                                                    onClick={() => handleSetExcludeState(true)}
                                                    sx={{
                                                        display: 'flex',
                                                        height: '24px',
                                                        padding: '9px var(--S-s-spacious, 12px)',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flex: '1 0 0',
                                                        borderRadius: '100px',
                                                        '&.active': {
                                                            background: 'var(--grey-palette-grey-6, #3A4B59)',
                                                            color: 'var(--grey-palette-white, #FFF)',
                                                        },
                                                        '&.in-active': {
                                                            border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                                            background: 'var(--grey-palette-white, #FFF)',
                                                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                                                        },
                                                    }}
                                                    className={'exclude-switch' + (exclude ? ' active' : ' in-active')}
                                                >
                                                    <span>Exclude</span>
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                    {haveFilters && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                padding: '0px 8px 4px 8px',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                                alignSelf: 'stretch',
                                                borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                                            }}
                                        >
                                            {(!!value?.length || !!currentAndPastValue?.length) && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        padding: '4px 0px',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        gap: '8px',
                                                        alignSelf: 'stretch',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            alignSelf: 'stretch',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                            }}
                                                        >
                                                            <span>Included:</span>
                                                            <Chip
                                                                deleteIcon={<X strokeWidth={1} size={12} />}
                                                                size="small"
                                                                label={[...value, ...currentAndPastValue]?.length || 0}
                                                                clickable
                                                                onDelete={() => {
                                                                    setValue([], 'current');
                                                                    setValue([], 'currentAndPast');
                                                                }}
                                                            />
                                                        </Box>
                                                        {[...value, ...currentAndPastValue]?.length > 3 &&
                                                            showAllIncluded && (
                                                                <Button
                                                                    size="small"
                                                                    endIcon={<ChevronUp strokeWidth={1} size={16} />}
                                                                    onClick={() => setShowAllIncluded(false)}
                                                                >
                                                                    Hide
                                                                </Button>
                                                            )}
                                                        {[...value, ...currentAndPastValue]?.length > 3 &&
                                                            !showAllIncluded && (
                                                                <Button
                                                                    size="small"
                                                                    endIcon={<ChevronDown strokeWidth={1} size={16} />}
                                                                    onClick={() => setShowAllIncluded(true)}
                                                                >
                                                                    Show all
                                                                </Button>
                                                            )}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            alignContent: 'flex-start',
                                                            gap: '6px',
                                                            alignSelf: 'stretch',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {[...value, ...currentAndPastValue]
                                                            ?.slice(0, showAllIncluded ? 9999 : 3)
                                                            .map((option, idx) => (
                                                                <Chip
                                                                    icon={
                                                                        currentAndPastValue.includes(option) ? (
                                                                            <Tooltip
                                                                                title="Current & Past"
                                                                                placement="top"
                                                                            >
                                                                                <Clock strokeWidth={1} size={16} />
                                                                            </Tooltip>
                                                                        ) : null
                                                                    }
                                                                    deleteIcon={<X strokeWidth={1} size={12} />}
                                                                    size="small"
                                                                    label={option?.name || option}
                                                                    clickable
                                                                    key={`include-${idx}-${option?.name || option}`}
                                                                    onDelete={() => {
                                                                        setValue(
                                                                            currentAndPastValue.includes(option)
                                                                                ? currentAndPastValue
                                                                                    ?.filter(
                                                                                        (val, filterIdx) =>
                                                                                            val !== option.name
                                                                                    )
                                                                                    .filter((val) => val !== option)
                                                                                : value
                                                                                    ?.filter(
                                                                                        (val, filterIdx) =>
                                                                                            val !== option.name
                                                                                    )
                                                                                    .filter((val) => val !== option),
                                                                            currentAndPastValue.includes(option)
                                                                                ? 'currentAndPast'
                                                                                : 'current'
                                                                        );
                                                                    }}
                                                                />
                                                            ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {(!!excludeValue?.length || !!currentAndPastExcludeValue?.length) && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        padding: '4px 0px',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        gap: '8px',
                                                        alignSelf: 'stretch',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            alignSelf: 'stretch',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                            }}
                                                        >
                                                            <span>Excluded:</span>
                                                            <Chip
                                                                deleteIcon={<X strokeWidth={1} size={12} />}
                                                                size="small"
                                                                label={
                                                                    [...excludeValue, ...currentAndPastExcludeValue]
                                                                        ?.length || 0
                                                                }
                                                                clickable
                                                                onDelete={() => {
                                                                    setExcludeValue([], 'current');
                                                                    setExcludeValue([], 'currentAndPast');
                                                                }}
                                                            />
                                                        </Box>
                                                        {[...excludeValue, ...currentAndPastExcludeValue]?.length > 3 &&
                                                            showAllExcluded && (
                                                                <Button
                                                                    size="small"
                                                                    endIcon={<ChevronUp strokeWidth={1} size={16} />}
                                                                    onClick={() => setShowAllExcluded(false)}
                                                                >
                                                                    Hide
                                                                </Button>
                                                            )}
                                                        {[...excludeValue, ...currentAndPastExcludeValue]?.length > 3 &&
                                                            !showAllExcluded && (
                                                                <Button
                                                                    size="small"
                                                                    endIcon={<ChevronDown strokeWidth={1} size={16} />}
                                                                    onClick={() => setShowAllExcluded(true)}
                                                                >
                                                                    Show all
                                                                </Button>
                                                            )}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            alignContent: 'flex-start',
                                                            gap: '6px',
                                                            alignSelf: 'stretch',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {[...excludeValue, ...currentAndPastExcludeValue]
                                                            ?.slice(0, showAllExcluded ? 9999 : 3)
                                                            .map((option, idx) => (
                                                                <Chip
                                                                    icon={
                                                                        currentAndPastExcludeValue.includes(option) && (
                                                                            <Tooltip
                                                                                title="Current & Past"
                                                                                placement="top"
                                                                            >
                                                                                <Clock strokeWidth={1} size={16} />
                                                                            </Tooltip>
                                                                        )
                                                                    }
                                                                    deleteIcon={<X strokeWidth={1} size={12} />}
                                                                    size="small"
                                                                    label={option?.name || option}
                                                                    key={`exclude-${idx}-${option?.name || option}`}
                                                                    clickable
                                                                    onDelete={() =>
                                                                        setExcludeValue(
                                                                            currentAndPastExcludeValue.includes(option)
                                                                                ? currentAndPastExcludeValue
                                                                                    ?.filter(
                                                                                        (val, filterIdx) =>
                                                                                            val !== option.name
                                                                                    )
                                                                                    .filter((val) => val !== option)
                                                                                : excludeValue
                                                                                    ?.filter(
                                                                                        (val, filterIdx) =>
                                                                                            val !== option.name
                                                                                    )
                                                                                    .filter((val) => val !== option),
                                                                            currentAndPastExcludeValue.includes(option)
                                                                                ? 'currentAndPast'
                                                                                : 'current'
                                                                        )
                                                                    }
                                                                />
                                                            ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        />
                    </Box>
                </ClickAwayListener>
            </StyledPopper>
        </React.Fragment>
    );
};
type RangeFilterProps = {
    onMaxInputChange?: (value: number) => void;
    onMinInputChange?: (value: number) => void;
    value: { min: number; max: number };
    setValue: (value: { min: number; max: number }) => void;
    label: string;
    icon?: React.ReactNode;
    loading?: boolean;
    isOpen?: boolean;
    toggleOpen?: () => void;
};

export const RangeFilter = ({
    onMaxInputChange,
    onMinInputChange,
    value,
    setValue,
    label,
    icon,
    isOpen,
    toggleOpen,
}: RangeFilterProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        value && setValue(value);

        setAnchorEl(event.currentTarget);
        toggleOpen();
    };

    const handleClose = () => {
        if (anchorEl) {
            anchorEl.focus();
        }
        setAnchorEl(null);
        toggleOpen();
    };

    const handleClearClick = (e) => {
        e.stopPropagation();
        setValue({ min: 0, max: 0 });
    };

    // const open = Boolean(anchorEl);
    const open = isOpen;
    const id = open ? 'github-label' : undefined;
    const haveFilters = !!value?.min || !!value?.max;

    return (
        <React.Fragment>
            <ButtonBase
                sx={{
                    display: 'flex',
                    height: '40px',
                    padding: 'var(--S-s-regular, 8px) var(--S-s-spacious, 12px)',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 'var(--S-xs, 6px)',
                    borderRadius: 'var(--radius-round, 100px)',
                    background: 'var(--grey-palette-grey-6, #3A4B59)',
                    boxShadow: '0px 1px 2px 0px rgba(20, 21, 26, 0.06)',
                    color: 'var(--grey-palette-white, #FFF)',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily:
                        '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu","Cantarell", "Fira Sans", "Droid Sans", sans-serif !important',
                    '& svg': {
                        color: 'white',
                    },
                    '&.active': {
                        background: 'var(--grey-palette-grey-6, #3A4B59)',
                        color: 'var(--grey-palette-white, #FFF)',
                        '&:hover': {
                            background: 'var(--grey-palette-grey-6, #495e6f)',
                        },
                    },
                    '&.in-active': {
                        background: 'var(--grey-palette-white, #FFF)',
                        color: 'var(--grey-palette-grey-6, #3A4B59)',
                        border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                        '&:hover': {
                            background: 'rgba(21, 27, 38, 0.04)',
                        },
                        '& svg': {
                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                        },
                    },
                }}
                onClick={handleClick}
                className={haveFilters ? 'active' : 'in-active'}
            >
                {icon}
                {!haveFilters && label}
                {haveFilters &&
                    [value.min || 0, '-', value.max || 100].map((val) => (
                        <Box
                            sx={{
                                color: 'var(--grey-palette-white, #FFF)',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '21px /* 150% */',
                                maxWidth: '200px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'left',
                            }}
                        >
                            {val}
                        </Box>
                    ))}
                {!haveFilters && !open && <ChevronDown strokeWidth={1} size={20} />}
                {!haveFilters && open && <ChevronUp strokeWidth={1} size={20} />}
                {haveFilters && (
                    <IconButton size="small" onClick={handleClearClick}>
                        <CircleX strokeWidth={1} size={20} />
                    </IconButton>
                )}
            </ButtonBase>
            <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" transition={false}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box>
                        <Box
                            sx={{ display: 'flex', gap: 2, alignItems: 'center', minHeight: '100px', padding: '16px' }}
                        >
                            <TextField
                                label="Minimum"
                                type="number"
                                value={value.min}
                                onChange={(e) =>
                                    setValue({ ...value, min: Math.min(parseInt(e.target.value, 10), 100) })
                                }
                                inputProps={{ min: 0 }}
                                variant="outlined"
                                size="small"
                            />
                            <TextField
                                label="Maximum"
                                type="number"
                                value={value.max}
                                onChange={(e) =>
                                    setValue({ ...value, max: Math.min(parseInt(e.target.value, 10), 100) })
                                }
                                inputProps={{ min: 0 }}
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    </Box>
                </ClickAwayListener>
            </StyledPopper>
        </React.Fragment>
    );
};

const formatDomain = (domain) => {
    if (domain) {
        return `www.${domain}`;
    } else if (domain === null) {
        return 'No website';
    }
};

interface PopperComponentProps {
    anchorEl?: any;
    disablePortal?: boolean;
    open: boolean;
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
    width: '100% !important',
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        margin: 0,
        color: 'inherit',
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: 'auto',
            alignItems: 'flex-start',
            padding: 8,
            borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
            '&[aria-selected="true"]': {
                backgroundColor: 'transparent',
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
                backgroundColor: theme.palette.action.hover,
            },
        },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: 'relative',
    },
}));

function PopperComponent(props: PopperComponentProps) {
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
    border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
    boxShadow: `0 8px 24px ${theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'}`,
    borderRadius: '8px',
    width: 320,
    zIndex: theme.zIndex.modal,
    fontSize: 13,
    color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    marginTop: '4px !important',
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    paddingRight: 0,
    width: '100%',
    gap: '4px',
    '& input': {
        borderRadius: 4,
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
        padding: 8,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        border: 'none',
        fontSize: 14,
        outline: 0,
        '&:focus': {
            boxShadow: `0px 0px 0px 3px ${
                theme.palette.mode === 'light' ? 'rgba(3, 102, 214, 0.3)' : 'rgb(12, 45, 107)'
                }`,
            borderColor: theme.palette.mode === 'light' ? '#0366d6' : '#388bfd',
            border: 'none',
            outline: 0,
        },
    },
}));

export function filterOptionsHandler(getOptionLabel, freeSolo, options, params) {
    let filtered;
    const tempOptions = options || [];

    filtered =
        params.inputValue !== ''
            ? tempOptions?.filter((opt) => {
                if (getOptionLabel)
                    return getOptionLabel(opt)?.toLowerCase().includes(params.inputValue.toLowerCase());
                return opt?.toLowerCase().includes(params.inputValue.toLowerCase());
            })
            : tempOptions;

    if (freeSolo && params.inputValue !== '') {
        filtered.push({
            inputValue: params.inputValue,
            title: `Add "${params.inputValue.toLowerCase()}"`,
        });
    }
    return filtered;
}
